/*
  No2moro GB V1 - safe-price gate.
  Runs on the UK market only (theme.liquid). Do not expose Standard product
  prices or let product forms submit until the actual GB twin is selected.
  This is theme-level risk reduction, NOT server-side checkout validation.
*/
(() => {
  const html = document.documentElement;
  if (!html.classList.contains('n2m-prices-resolving')) return;

  const MARKER = '\u2060';
  let displayedRegion = null;
  let lastVariant = null;
  let variantPending = false;
  let timer = null;
  let stableSince = 0;
  let lastPageRegion = '';
  let running = false;

  function readGeo() {
    return String(window.No2moroGeoRegion || '').toLowerCase();
  }

  const startedAt = Date.now();
  function destinationRegion() {
    const geo = readGeo();
    if (geo === 'ni') return 'standard';
    if (geo === 'gb') return 'gb';
    // The UK storefront is an explicit selected market. An ASL zone can
    // report generic 'standard', or not report a custom value at all.
    // Never hide UK prices indefinitely; allow 1.8 seconds for explicit
    // NI detection to arrive before applying the selected UK market.
    const selectedCountry = String(window.No2moroRegionalPricingConfig?.country ||
      window.Shopify?.country || '').toUpperCase();
    if (selectedCountry === 'GB' && Date.now() - startedAt >= 1800) return 'gb';
    return null;
  }

  function verifyProduct(desired) {
    const product = document.querySelector('#MainContent product-info');
    if (!product) return true;
    if (product.dataset.no2moroPricingRegion !== desired) return false;

    const chooser = product.querySelector('variant-selects');
    if (!chooser) return true;
    const options = Array.from(chooser.querySelectorAll('input[type=radio], select option'));
    const hasTwin = options.some(o => String(o.value || '').includes(MARKER));
    if (!hasTwin) return true;

    const id = product.querySelector('form[is="product-form"] input[name="id"]')?.value
      || product.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;
    if (!id) return false;

    const text = chooser.querySelector('[data-selected-variant]')?.textContent;
    let variant = null;
    try { variant = JSON.parse(text || 'null'); } catch(e) { return false; }
    const eventVariant = lastVariant && String(lastVariant.id) === String(id)
      ? lastVariant : null;
    const candidate = eventVariant || variant;
    if (!candidate || String(candidate.id) !== String(id)) return false;
    const candidateIsGb = String(candidate.title || '').includes(MARKER);
    if (candidateIsGb !== (desired === 'gb')) return false;
    const regionField = product.querySelector('form[is="product-form"] [name="properties[_pricing_region]"]');
    if (regionField && regionField.value !== (desired === 'gb' ? 'GB' : 'STANDARD')) return false;
    return true;
  }

  function verifyCards(desired) {
    const roots = document.querySelectorAll('[data-no2moro-card-add]');
    for (const root of roots) {
      if (root.dataset.no2moroPricingRegion !== desired) return false;
      const sizes = Array.from(root.querySelectorAll('[data-no2moro-card-size]'));
      const hasTwin = sizes.some(button => String(button.dataset.no2moroPricingRegion) === 'gb');
      if (hasTwin && !sizes.some(button => button.dataset.no2moroRegionHidden === 'false' &&
          button.dataset.no2moroPricingRegion === desired)) return false;
    }
    return true;
  }

  function trusted() {
    const desired = destinationRegion();
    if (!desired) return false; // No guessing based on UK market or Zone One.
    const engine = window.No2moroRegionalPricing;
    if (!engine || engine.getRegion() !== desired) return false;
    if (html.dataset.no2moroPricingRegion !== desired) return false;
    // Main product prices must not be held hostage by unrelated cards below
    // the fold (some cards have no selectable sizes/are updated asynchronously).
    // Card forms get their own regional check in protect().
    return verifyProduct(desired);
  }

  function resolve() {
    // NI must remain on the UK/GBP Shopify market but use Standard
    // (ROI-equivalent) product variants. Never redirect NI into EUR.
    // Keep prices hidden until the variant matches the resolved region.
    // Keep the storefront variant selector in sync with the resolved region.
    // Calling setRegion only when needed avoids recursive region-change events.
    const desired = destinationRegion();
    const engine = window.No2moroRegionalPricing;
    if (desired && engine && engine.getRegion() !== desired) {
      engine.setRegion(desired, { persist: false });
      return;
    }
    const ok = trusted();
    const regionState = readGeo() + '/' + (window.No2moroRegionalPricing?.getRegion() || '');
    if (regionState !== lastPageRegion) { lastPageRegion = regionState; stableSince = 0; }

    if (!ok) {
      stableSince = 0;
      if (displayedRegion) {
        displayedRegion = null;
        html.classList.add('n2m-prices-resolving');
      }
      return;
    }

    if (!stableSince) stableSince = Date.now();
    // Let the theme finish replacing the AJAX price nodes before unveiling.
    if (Date.now() - stableSince >= 320) {
      displayedRegion = destinationRegion();
      html.classList.remove('n2m-prices-resolving', 'n2m-prices-delayed');
    }
  }

  function protect(e) {
    const target = e.target;
    const card = target?.closest?.('[data-no2moro-card-add]');
    // Never allow a card purchase with stale regional variant buttons.
    const desired = destinationRegion();
    const cardReady = !card || card.dataset.no2moroPricingRegion === desired;
    if (trusted() && displayedRegion === desired && cardReady) return;
    const form = target.closest?.('form');
    const purchaseForm = form && (form.matches('form[is="product-form"]') ||
      form.matches('form[action*="/cart/add"]') ||
      form.closest('[data-no2moro-card-add]'));
    if (purchaseForm || (e.type === 'click' && target.closest?.(
      '.product-form__buttons, [data-no2moro-card-add], sticky-atc-bar .shopify-payment-button'
    ))) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }

  // Capture form submissions, including theme's programmatic requestSubmit.
  document.addEventListener('submit', protect, true);
  document.addEventListener('click', protect, true);
  document.addEventListener('variant:changed', e => { variantPending = false; lastVariant = e.detail?.variant || null; displayedRegion = null; html.classList.add('n2m-prices-resolving'); resolve(); }, true);
  document.addEventListener('change', e => {
    if (e.target.closest?.('#MainContent product-info variant-selects')) {
      variantPending = true;
      displayedRegion = null; stableSince = 0;
      html.classList.add('n2m-prices-resolving');
    }
  }, true);
  window.addEventListener('no2moro:pricing-region-change', resolve);
  window.addEventListener('no2moro:pricing-region-applied', resolve);
  document.addEventListener('DOMContentLoaded', resolve);
  timer = setInterval(resolve, 120);

  setTimeout(() => {
    if (displayedRegion) return;
    html.classList.add('n2m-prices-delayed');
    const box = document.querySelector('#MainContent product-info .f-price')?.parentElement;
    if (box && !box.querySelector('#n2m-price-delay-message')) {
      const el = document.createElement('p');
      el.id = 'n2m-price-delay-message';
      el.textContent = 'Checking local prices. Please refresh if this takes longer than expected.';
      box.appendChild(el);
    }
  }, 6500);

  resolve();
})();